import { createPortal } from 'react-dom';
import { AnimatePresence, m } from '../../lib/motion.js';
import { useUi } from '../../stores/ui.js';
import { cn } from '../../lib/format.js';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * Toasts. PRD §8: a button that says "Place order" produces a toast that says
 * "Order placed." — past tense, stating what happened rather than congratulating
 * the shopper.
 *
 * The region is a live region so a screen reader announces without stealing
 * focus, and it sits above the mobile navigation rather than under it.
 */

/**
 * Tone is carried by a solid rail down the leading edge and by the icon, not by
 * a translucent wash over the card. A tinted background at 8% alpha sits over
 * whatever the toast happens to be covering, so it reads as a different colour
 * on a product image than it does on the page ground — which looks like a
 * rendering fault rather than a state.
 */
const rails = {
  default: 'bg-mist',
  success: 'bg-success',
  danger: 'bg-danger',
};

const icons = {
  success: (
    <svg viewBox="0 0 16 16" className="size-4 text-success" fill="currentColor" aria-hidden="true">
      <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm3.53 5.28-4.2 4.2a.75.75 0 0 1-1.06 0L4.47 8.68 5.53 7.6l1.27 1.27 3.67-3.66 1.06 1.06Z" />
    </svg>
  ),
  danger: (
    <svg viewBox="0 0 16 16" className="size-4 text-danger" fill="currentColor" aria-hidden="true">
      <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm0 3.25a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0V5a.75.75 0 0 1 .75-.75ZM8 11.75a.95.95 0 1 1 0-1.9.95.95 0 0 1 0 1.9Z" />
    </svg>
  ),
};

/** Portals need a live document; see the note in Sheet.jsx. */
const portalTarget = () => (typeof document === 'undefined' ? null : document.body);

export const Toaster = () => {
  const toasts = useUi((state) => state.toasts);
  const dismiss = useUi((state) => state.dismissToast);
  const reduced = useReducedMotion();
  const target = portalTarget();

  if (!target) return null;

  return createPortal(
    <div
      role="region"
      aria-live="polite"
      aria-label="Notifications"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:bottom-0 sm:items-end"
    >
      <AnimatePresence initial={false}>
        {toasts.map((entry) => (
          <m.div
            key={entry.id}
            layout={!reduced}
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: reduced ? 0.15 : 0.24, ease: [0.25, 1, 0.5, 1] }}
            className="pointer-events-auto relative flex w-full max-w-sm items-start gap-3 overflow-hidden border border-mist bg-surface py-3 pr-3 pl-4 text-ink shadow-[0_16px_40px_-16px_rgba(20,23,26,0.4)]"
          >
            {/* The tone rail. Solid, so it reads the same over any background. */}
            <span
              aria-hidden="true"
              className={cn('absolute inset-y-0 left-0 w-[3px]', rails[entry.tone] ?? rails.default)}
            />

            {entry.image ? (
              <img
                src={entry.image}
                alt=""
                width="44"
                height="56"
                loading="lazy"
                className="h-14 w-11 shrink-0 bg-shell object-cover"
              />
            ) : (
              icons[entry.tone] && <span className="mt-0.5 shrink-0">{icons[entry.tone]}</span>
            )}

            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-xs font-medium">
                {entry.image && icons[entry.tone] && (
                  <span className="shrink-0">{icons[entry.tone]}</span>
                )}
                {entry.title}
              </p>
              {entry.description && (
                <p className="mt-0.5 truncate text-2xs text-muted">{entry.description}</p>
              )}
              {entry.meta && <p className="mt-0.5 text-2xs text-muted tabular-nums">{entry.meta}</p>}
              {entry.action && (
                <button
                  type="button"
                  onClick={() => {
                    entry.action.onClick?.();
                    dismiss(entry.id);
                  }}
                  className="mt-2 text-2xs font-medium text-ink underline decoration-mist underline-offset-4 hover:decoration-ink"
                >
                  {entry.action.label}
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => dismiss(entry.id)}
              aria-label="Dismiss"
              className="-mt-0.5 -mr-1 grid size-6 shrink-0 place-items-center rounded text-muted transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <svg viewBox="0 0 16 16" className="size-3" fill="currentColor" aria-hidden="true">
                <path d="M4.3 3.24 8 6.94l3.7-3.7 1.06 1.06-3.7 3.7 3.7 3.7-1.06 1.06-3.7-3.7-3.7 3.7-1.06-1.06 3.7-3.7-3.7-3.7z" />
              </svg>
            </button>
          </m.div>
        ))}
      </AnimatePresence>
    </div>,
    target,
  );
};

export default Toaster;
